<script setup lang="ts">
import { computed, ref } from "vue";

import { downloadFile } from "@recordsure/unity-stk";

import ConfirmPopover from "@/components/ConfirmPopover.vue";

import { config } from "@/config";

import { authMachine } from "@/machines/auth-machine";

import { useMessageStore } from "@/stores";
import { jobsService } from "@/services";

import type { Job } from "@/models/Job";

withDefaults(defineProps<{ job: Job }>(), {  })

const emit = defineEmits<{ refresh: [] }>();

const isRetrying = ref(false);
const isDeleting = ref(false);
const isDownloading = ref<AbortController>();

const createdDate = computed(() =>
  Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "medium" }).format(new Date(__props.job.requested)),
);

const expiryDate = computed(() => {
  if (__props.job.expires == null) return "-";

  const daysLeft = Math.ceil((new Date(__props.job.expires).getTime() - Date.now()) / (24 * 60 * 60 * 1000));

  // The API already filters expired jobs out
  if (daysLeft < 0) return "Expired";

  if (daysLeft === 0) return "Expires today";

  return `in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`;
});

const parsedParameters = computed(() => {
  if (__props.job.parameters === "" || __props.job.parameters == null) return undefined;

  try {
    const result = {} as Record<string, unknown>;
    const parsed = JSON.parse(__props.job.parameters) as Record<string, unknown>;

    for (const [k, v] of Object.entries(parsed)) {
      if (v == null) continue;
      result[k] = v;
    }

    if (Object.keys(result).length === 0) return undefined;

    return result;
  } catch {
    return undefined;
  }
});

async function handleRetry() {
  try {
    isRetrying.value = true;
    await jobsService.RetryJob(__props.job.clientName, __props.job.brand, __props.job.jobId);

    useMessageStore().updateMessage({
      type: "success",
      title: "Jobs",
      message: `Retry requested for "${__props.job.fileType}".`,
      autoHide: true,
      showReload: false,
    });

    emit("refresh");
  } catch {
    useMessageStore().updateMessage({
      type: "error",
      title: "Jobs",
      message: "Failed to retry job. Please try again.",
      autoHide: true,
      showReload: false,
    });
  } finally {
    isRetrying.value = false;
  }
}

async function handleDelete() {
  try {
    isDeleting.value = true;

    await jobsService.DeleteJob(__props.job.clientName, __props.job.brand, __props.job.jobId);

    useMessageStore().updateMessage({
      type: "success",
      title: "Jobs",
      message: `"${__props.job.fileType}" has been deleted.`,
      autoHide: true,
      showReload: false,
    });

    emit("refresh");
  } catch (e) {
    useMessageStore().updateMessage({
      type: "error",
      title: "Jobs",
      message: "Failed to delete job. Please try again.",
      autoHide: true,
      showReload: false,
    });
  } finally {
    isDeleting.value = false;
  }
}

async function handleDownload() {
  const token = authMachine.getSnapshot().context.token?.access_token;
  if (token == null) return;

  isDownloading.value?.abort();
  isDownloading.value = new AbortController();

  try {
    await downloadFile(
      `${config.apiUrl}/api/My/jobs/ById/${__props.job.clientName}/${__props.job.jobId}${__props.job.brand == null ? "" : `?brand=${__props.job.brand}`}`,
      { accessToken: token, ssoApiUrl: config.ssoApiUrl, signal: isDownloading.value.signal },
    );
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") return;

    useMessageStore().updateMessage({
      type: "error",
      title: "Jobs",
      message: "Failed to download job file. Please try again.",
      autoHide: true,
      showReload: false,
    });
  } finally {
    isDownloading.value = undefined;
  }
}
</script>
<template>
  <tr class="tw-divide-x tw-border-b last:tw-border-b-0 [&>*]:tw-p-2">
    <td class="tw-whitespace-nowrap">
      {{ createdDate }}
    </td>
    <td>{{ job.fileType }}</td>
    <td class="tw-whitespace-nowrap">
      <span
        class="tw-inline-block tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold"
        :class="{
          'tw-bg-red-100 tw-text-red-800': job.status === 'failed',
          'tw-bg-gray-200 tw-text-gray-700': job.status === 'queued',
          'tw-bg-blue-100 tw-text-blue-800': job.status === 'processing',
          'tw-bg-green-100 tw-text-green-800': job.status === 'completed',
          'tw-bg-orange-100 tw-text-orange-800': job.status === 'deleting' || isDeleting,
        }"
      >
        <template v-if="job.status === 'deleting' || isDeleting"> Deleting </template>
        <template v-else-if="job.status === 'queued'"> Your file is queued for processing </template>
        <template v-else-if="job.status === 'processing'">
          <i aria-hidden="true" class="fas fa-circle-notch fa-spin tw-mr-1" />Processing
        </template>
        <template v-else-if="job.status === 'failed'">File generation failed</template>
        <template v-else-if="job.status === 'completed'">Completed</template>
      </span>
    </td>
    <td class="tw-uppercase">{{ job.extension ?? "-" }}</td>
    <td class="tw-whitespace-nowrap">
      {{ expiryDate }}
    </td>
    <td>
      <template v-if="parsedParameters != null">
        <details>
          <summary class="tw-cursor-pointer tw-text-xs tw-text-blue-600 hover:tw-underline">View</summary>
          <dl class="tw-mt-1 tw-text-xs">
            <template v-for="(v, k) in parsedParameters" :key="k">
              <dt class="tw-font-semibold">{{ k }}</dt>
              <dd class="tw-ml-2 tw-text-gray-600">{{ v }}</dd>
            </template>
          </dl>
        </details>
      </template>
      <span v-else class="tw-text-gray-400">-</span>
    </td>
    <td>
      <div class="tw-flex tw-items-center tw-gap-2 *:tw-min-w-max">
        <button v-if="job.status === 'completed'" type="button" class="tw-btn-md-white tw-b-r" @click="handleDownload">
          <i
            aria-hidden="true"
            class="tw-mr-1"
            :class="isDownloading != null ? 'fas fa-circle-notch fa-spin' : 'fas fa-download'"
          />
          {{ isDownloading != null ? "Cancel" : "Download" }}
        </button>
        <button
          v-if="job.status === 'failed'"
          type="button"
          class="tw-btn-md-white tw-b-r"
          :disabled="isRetrying"
          @click="handleRetry"
        >
          <i aria-hidden="true" class="tw-mr-1" :class="isRetrying ? 'fas fa-circle-notch fa-spin' : 'fas fa-redo'" />
          Retry
        </button>
        <span v-if="job.status === 'deleting' || isDeleting" class="tw-text-xs tw-italic tw-text-gray-500">
          Deleting...
        </span>
        <ConfirmPopover v-else :trigger-props="{ asChild: true }" @confirmed="handleDelete">
          <template #trigger="{ open }">
            <button type="button" class="tw-btn-md-white tw-b-r" @click="open">
              <i aria-hidden="true" class="fas fa-trash tw-mr-1" />Delete
            </button>
          </template>
          <template #question>Are you sure you want to delete this job?</template>
        </ConfirmPopover>
      </div>
    </td>
  </tr>
</template>
